import { c as _c } from "react/compiler-runtime";
import { InfoTooltip } from '@morpho-org/ui-v6/client';
import { memo, useEffect, useMemo, useRef } from 'react';
import { InternalLink } from '@/components/common/InternalLink';
import MarketTrustedByCuratorStack, { type MarketTrustedByCuratorStackProps } from '@/components/MarketPage/components/MarketTrustedByCuratorStack/MarketTrustedByCuratorStack';
import { getCuratorUrl } from '@/helpers/routing';
import { trackMarketTrustedByCuratorClicked, trackMarketTrustedByViewed } from '@/lib/analytics/client/trackers/market-trusted-by';
const MAX_VISIBLE_CURATORS = 5;

/** Measured against the copy beside it: ~24px at rest, ~4px once hover shifts the stack 20px. */
const TRUSTED_BY_TRAILING_SPACE = 20;
interface MarketTrustedByProps {
  marketId: string;
  chainId: number;
  isUnverifiedMarket: boolean;
  curators: MarketTrustedByCuratorStackProps['trustedByCurators'];
}
const MarketTrustedBy = t0 => {
  const $ = _c(15);
  const {
    marketId,
    chainId,
    isUnverifiedMarket,
    curators
  } = t0;
  let t1;
  if ($[0] !== curators) {
    t1 = curators.slice(0, MAX_VISIBLE_CURATORS);
    $[0] = curators;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const visibleCurators = t1;
  const remainingCuratorCount = Math.max(curators.length - visibleCurators.length, 0);
  const rootRef = useRef(null);
  const trackedMarketKey = useRef(null);
  let t2;
  let t3;
  if ($[2] !== chainId || $[3] !== curators.length || $[4] !== isUnverifiedMarket || $[5] !== marketId) {
    t2 = () => {
      const marketKey = `${chainId}:${marketId}`;
      if (isUnverifiedMarket || curators.length === 0 || trackedMarketKey.current === marketKey) {
        return;
      }
      const node = rootRef.current;
      if (!node) {
        return;
      }
      const observer = new IntersectionObserver(t4 => {
        const [entry] = t4;
        if (!entry?.isIntersecting) {
          return;
        }
        trackedMarketKey.current = marketKey;
        trackMarketTrustedByViewed({
          marketId,
          chainId,
          curatorCount: curators.length
        });
        observer.disconnect();
      }, {
        threshold: 0.1
      });
      observer.observe(node);
      return () => observer.disconnect();
    };
    t3 = [isUnverifiedMarket, curators.length, marketId, chainId];
    $[2] = chainId;
    $[3] = curators.length;
    $[4] = isUnverifiedMarket;
    $[5] = marketId;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== chainId || $[9] !== curators || $[10] !== isUnverifiedMarket || $[11] !== marketId || $[12] !== remainingCuratorCount || $[13] !== visibleCurators) {
    t4 = <div ref={rootRef} className="gap-sm flex w-fit max-w-full flex-wrap items-center">{isUnverifiedMarket ? <div className="gap-2xs flex items-center"><span className="text-warning-foreground text-xs" style={{
          whiteSpace: "nowrap"
        }}>Unverified market</span><div className="relative top-[-1px]"><InfoTooltip tooltipContent="Review risk parameters before opening a position." /></div><p className="text-secondary-foreground max-w-[560px] text-xs leading-normal">No vault curators deposit in this market.</p></div> : <><MarketTrustedByCuratorStack trustedByCurators={curators} maxTokensCount={MAX_VISIBLE_CURATORS} trailingSpace={TRUSTED_BY_TRAILING_SPACE} /><p className="text-secondary-foreground max-w-[560px] text-xs leading-normal">Trusted by{" "}{visibleCurators.map((curator, index) => {
            const isLastVisibleCurator = index === visibleCurators.length - 1;
            const separator = remainingCuratorCount > 0 || !isLastVisibleCurator ? "," : "";
            return <span key={curator.id}><InternalLink href={getCuratorUrl({
                curatorId: curator.id
              })} className="text-secondary-foreground hover:text-foreground text-xs" onClick={() => trackMarketTrustedByCuratorClicked({
                marketId,
                chainId,
                curatorId: curator.id,
                curatorName: curator.name
              })}>{curator.name}</InternalLink>{separator ? `${separator} ` : ""}</span>;
          })}{remainingCuratorCount > 0 && <span className="text-secondary-foreground text-xs">and {remainingCuratorCount} other{" "}{remainingCuratorCount === 1 ? "curator" : "curators"}</span>}</p></>}</div>;
    $[8] = chainId;
    $[9] = curators;
    $[10] = isUnverifiedMarket;
    $[11] = marketId;
    $[12] = remainingCuratorCount;
    $[13] = visibleCurators;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
export default memo(MarketTrustedBy);